export const PROJECTION_SCHEMA_VERSION = 1;

export const PROJECTION_OWNERS = Object.freeze({
  ACCOUNT: "account",
  AUTHORIZATION: "authorization",
  TELEPHONY: "telephony",
  SCRIPT: "script",
  RUN: "run",
  CRM: "crm",
});

export const EVIDENCE_KINDS = Object.freeze({
  KNOWN: "known",
  PENDING: "pending",
  FAILURE: "failure",
  MISSING: "missing",
  RESTRICTED: "restricted",
  CONFLICTING: "conflicting",
  UNKNOWN: "unknown",
  RECONCILIATION_REQUIRED: "reconciliation-required",
});

export const PROJECTION_FIELD_ALLOWLISTS = Object.freeze({
  [PROJECTION_OWNERS.ACCOUNT]: Object.freeze(["status"]),
  [PROJECTION_OWNERS.AUTHORIZATION]: Object.freeze(["visibility"]),
  [PROJECTION_OWNERS.TELEPHONY]: Object.freeze([
    "sessionState",
    "presenceState",
    "connectionState",
    "callState",
  ]),
  [PROJECTION_OWNERS.SCRIPT]: Object.freeze(["assignmentState"]),
  [PROJECTION_OWNERS.RUN]: Object.freeze(["runState"]),
  [PROJECTION_OWNERS.CRM]: Object.freeze(["resolutionState"]),
});

const PROHIBITED_FIELD_PATTERN = /(?:password|passwd|secret|token|credential|api[_-]?key|provider[_-]?(?:payload|native|url|phone|email)|recording)/i;
const VALID_EVIDENCE_KINDS = new Set(Object.values(EVIDENCE_KINDS));
const VALID_OWNERS = new Set(Object.values(PROJECTION_OWNERS));

function isPlainObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

// Item 7 correction: must also descend into arrays (e.g. Calendar's
// appointments[], Notes' notes[], Discovery/Quote's fields[]/values[]) —
// found via a WORKSPACE-AUDIT-007 §13 item 7 test proving prohibited-field
// detection actually holds for the array-shaped tool data ENG-002's
// prohibited-field invariant was extended to cover in items 2/4. Before
// this fix, a credential-shaped key nested inside an array item was never
// inspected at all, since a bare array is not a plain object.
function findProhibitedField(value, path = "data") {
  if (Array.isArray(value)) {
    for (const item of value) {
      const prohibited = findProhibitedField(item, `${path}[]`);
      if (prohibited) return prohibited;
    }
    return null;
  }
  if (!isPlainObject(value)) return null;
  for (const [key, nested] of Object.entries(value)) {
    const currentPath = `${path}.${key}`;
    if (PROHIBITED_FIELD_PATTERN.test(key)) return currentPath;
    const prohibited = findProhibitedField(nested, currentPath);
    if (prohibited) return prohibited;
  }
  return null;
}

export function validateProjectionEnvelope(
  projection,
  { expectedAccountId, logicalNow } = {}
) {
  const errors = [];
  if (!isPlainObject(projection)) {
    return { valid: false, errors: ["projection must be an object"] };
  }

  if (projection.schemaVersion !== PROJECTION_SCHEMA_VERSION) {
    errors.push("schemaVersion is unsupported");
  }
  if (!projection.projectionId || typeof projection.projectionId !== "string") {
    errors.push("projectionId is required");
  }
  if (!VALID_OWNERS.has(projection.owner)) errors.push("owner is invalid");
  if (!projection.accountId || typeof projection.accountId !== "string") {
    errors.push("accountId is required");
  }
  if (expectedAccountId && projection.accountId !== expectedAccountId) {
    errors.push("account scope mismatch");
  }
  if (!Number.isInteger(projection.accountEpoch) || projection.accountEpoch < 1) {
    errors.push("accountEpoch must be a positive integer");
  }
  if (!Number.isInteger(projection.revision) || projection.revision < 0) {
    errors.push("revision must be a non-negative integer");
  }
  if (!Number.isFinite(projection.observedAt)) errors.push("observedAt is invalid");
  if (!Number.isFinite(projection.freshUntil)) errors.push("freshUntil is invalid");
  if (
    Number.isFinite(projection.observedAt) &&
    Number.isFinite(projection.freshUntil) &&
    projection.freshUntil < projection.observedAt
  ) {
    errors.push("freshUntil precedes observedAt");
  }
  if (Number.isFinite(logicalNow) && projection.freshUntil < logicalNow) {
    errors.push("projection is stale");
  }
  if (
    !isPlainObject(projection.provenance) ||
    projection.provenance.kind !== "fake" ||
    !projection.provenance.source
  ) {
    errors.push("fake provenance is required");
  }
  if (
    projection.authorizationDecisionRef !== null &&
    typeof projection.authorizationDecisionRef !== "string"
  ) {
    errors.push("authorizationDecisionRef is invalid");
  }
  if (!isPlainObject(projection.evidence) || !VALID_EVIDENCE_KINDS.has(projection.evidence.kind)) {
    errors.push("evidence kind is invalid");
  } else if (
    [EVIDENCE_KINDS.UNKNOWN, EVIDENCE_KINDS.CONFLICTING, EVIDENCE_KINDS.RECONCILIATION_REQUIRED].includes(
      projection.evidence.kind
    ) &&
    (!projection.evidence.reason || typeof projection.evidence.reason !== "string")
  ) {
    errors.push("indeterminate evidence requires a reason");
  }

  if (!isPlainObject(projection.data)) {
    errors.push("data must be an object");
  } else {
    const prohibited = findProhibitedField(projection.data);
    if (prohibited) errors.push(`prohibited field: ${prohibited}`);
    const allowed = PROJECTION_FIELD_ALLOWLISTS[projection.owner] || [];
    const unexpected = Object.keys(projection.data).find((field) => !allowed.includes(field));
    if (unexpected) errors.push(`field is not allowlisted: data.${unexpected}`);
  }

  return { valid: errors.length === 0, errors };
}

export function assertProjectionEnvelope(projection, options) {
  const result = validateProjectionEnvelope(projection, options);
  if (!result.valid) throw new Error(result.errors.join("; "));
  return projection;
}

// --- WORKSPACE-AUDIT-007 §13 item 2: per-tool field allowlist tier ---
//
// Distinct from PROJECTION_FIELD_ALLOWLISTS above, which is the coarse,
// status-only allowlist workspaceStateStore.js composes from and must not
// be widened. This tier expresses the minimal, reviewed field shape each
// of the five read-only Agent Toolbar tools named in ENG-008 §3 may
// project, one dot-path per field, reviewed directly against the
// corresponding fixture in workspaceFixtures.js (LEAD_FIXTURE,
// DISCOVERY_FIXTURE, QUOTE_FIXTURE, CALENDAR_FIXTURE, NOTES_FIXTURE) —
// which this module does not import, to keep the contract the thing
// fixtures are validated against, not a fixture consumer. Array-of-object
// fields use a "[]" segment (e.g. "appointments[].phone") for the shape
// every array entry must satisfy. Script/Run identity is out of scope for
// this tier (WORKSPACE-AUDIT-007 §13 item 6).
export const WORKSPACE_TOOLS = Object.freeze({
  LEAD: "lead",
  DISCOVERY: "discovery",
  QUOTE: "quote",
  CALENDAR: "calendar",
  NOTES: "notes",
});

// Authoritative owner per WORKSPACE-AUDIT-007 §8. Notes is recorded as CRM
// per CRM-RUNTIME-001 §63's primary statement of ownership, though that
// section notes note ownership may split further across Run/Lead/Contact/
// Household/Appointment/compliance/provider note classes — not modeled by
// the single NOTES_FIXTURE this tier reviews.
export const WORKSPACE_TOOL_OWNERS = Object.freeze({
  [WORKSPACE_TOOLS.LEAD]: PROJECTION_OWNERS.CRM,
  [WORKSPACE_TOOLS.DISCOVERY]: PROJECTION_OWNERS.RUN,
  [WORKSPACE_TOOLS.QUOTE]: PROJECTION_OWNERS.RUN,
  [WORKSPACE_TOOLS.CALENDAR]: PROJECTION_OWNERS.CRM,
  [WORKSPACE_TOOLS.NOTES]: PROJECTION_OWNERS.CRM,
});

export const WORKSPACE_TOOL_FIELD_ALLOWLISTS = Object.freeze({
  [WORKSPACE_TOOLS.LEAD]: Object.freeze([
    "customerInformation",
    "customerInformation.firstName",
    "customerInformation.middleInitial",
    "customerInformation.lastName",
    "customerInformation.gender",
    "customerInformation.dob",
    "customerInformation.phone",
    "customerInformation.alternatePhone",
    "customerInformation.email",
    "address",
    "address.address1",
    "address.address2",
    "address.address3",
    "address.city",
    "address.state",
    "address.postalCode",
    "address.province",
    "leadInformation",
    "leadInformation.vendorId",
    "leadInformation.leadId",
    "leadInformation.comments",
  ]),
  [WORKSPACE_TOOLS.DISCOVERY]: Object.freeze([
    "subjects",
    "subjects[].id",
    "subjects[].name",
    "subjects[].kind",
    "subjects[].sort_order",
    "fields",
    "fields[].id",
    "fields[].label",
    "fields[].field_type",
    "fields[].required",
    "fields[].scope",
    "fields[].sort_order",
    "fields[].options",
    "values",
    "values[].field_id",
    "values[].subject_id",
    "values[].value",
  ]),
  [WORKSPACE_TOOLS.QUOTE]: Object.freeze([
    "subjects",
    "subjects[].id",
    "subjects[].name",
    "subjects[].kind",
    "subjects[].sort_order",
    "options",
    "fields",
    "fields[].id",
    "fields[].label",
    "fields[].kind",
    "fields[].field_type",
    "fields[].sort_order",
    "fields[].options",
    "fields[].include_in_household",
    "values",
    "values[].field_id",
    "values[].subject_id",
    "values[].option_index",
    "values[].value",
  ]),
  [WORKSPACE_TOOLS.CALENDAR]: Object.freeze([
    "selectedDate",
    "appointments",
    "appointments[].id",
    "appointments[].starts_at",
    "appointments[].ends_at",
    "appointments[].title",
    "appointments[].lead_name",
    "appointments[].phone",
    "appointments[].status",
  ]),
  [WORKSPACE_TOOLS.NOTES]: Object.freeze([
    "currentUser",
    "currentUser.id",
    "currentUser.name",
    "notes",
    "notes[].id",
    "notes[].body",
    "notes[].created_at",
    "notes[].created_by_user_id",
    "notes[].created_by_name",
  ]),
});

const VALID_WORKSPACE_TOOLS = new Set(Object.values(WORKSPACE_TOOLS));

function collectToolFieldPaths(value, path, out) {
  if (Array.isArray(value)) {
    for (const item of value) {
      if (isPlainObject(item)) collectToolFieldPaths(item, `${path}[]`, out);
    }
    return;
  }
  if (!isPlainObject(value)) return;
  for (const [key, nested] of Object.entries(value)) {
    const currentPath = path ? `${path}.${key}` : key;
    out.push(currentPath);
    collectToolFieldPaths(nested, currentPath, out);
  }
}

export function validateWorkspaceToolFields(tool, data) {
  if (!VALID_WORKSPACE_TOOLS.has(tool)) {
    return { valid: false, errors: [`unknown workspace tool: ${tool}`] };
  }
  if (!isPlainObject(data)) {
    return { valid: false, errors: ["data must be an object"] };
  }
  const allowed = new Set(WORKSPACE_TOOL_FIELD_ALLOWLISTS[tool]);
  const paths = [];
  collectToolFieldPaths(data, "", paths);
  const errors = paths
    .filter((path) => !allowed.has(path))
    .map((path) => `field is not allowlisted: data.${path}`);
  return { valid: errors.length === 0, errors };
}

// --- WORKSPACE-AUDIT-007 §13 item 4: tool-projection envelope wiring ---
//
// validateWorkspaceToolProjection mirrors validateProjectionEnvelope's
// shape checks (schemaVersion, projectionId, accountId/accountEpoch,
// revision, observedAt/freshUntil, provenance, evidence-kind validity),
// but gates `data` through the reviewed per-tool allowlist tier above
// (validateWorkspaceToolFields) instead of the coarse
// PROJECTION_FIELD_ALLOWLISTS, and requires `owner` to match the tool's
// reviewed WORKSPACE_TOOL_OWNERS entry exactly rather than any valid
// owner. Kept as a separate function rather than a shared refactor of
// validateProjectionEnvelope so the already-reviewed coarse validator
// (and everything that depends on its exact behavior) is not touched.
export function validateWorkspaceToolProjection(
  tool,
  projection,
  { expectedAccountId, logicalNow } = {}
) {
  if (!VALID_WORKSPACE_TOOLS.has(tool)) {
    return { valid: false, errors: [`unknown workspace tool: ${tool}`] };
  }
  if (!isPlainObject(projection)) {
    return { valid: false, errors: ["projection must be an object"] };
  }

  const errors = [];
  if (projection.schemaVersion !== PROJECTION_SCHEMA_VERSION) {
    errors.push("schemaVersion is unsupported");
  }
  if (!projection.projectionId || typeof projection.projectionId !== "string") {
    errors.push("projectionId is required");
  }
  const expectedOwner = WORKSPACE_TOOL_OWNERS[tool];
  if (projection.owner !== expectedOwner) {
    errors.push(`owner is invalid: expected ${expectedOwner}`);
  }
  if (!projection.accountId || typeof projection.accountId !== "string") {
    errors.push("accountId is required");
  }
  if (expectedAccountId && projection.accountId !== expectedAccountId) {
    errors.push("account scope mismatch");
  }
  if (!Number.isInteger(projection.accountEpoch) || projection.accountEpoch < 1) {
    errors.push("accountEpoch must be a positive integer");
  }
  if (!Number.isInteger(projection.revision) || projection.revision < 0) {
    errors.push("revision must be a non-negative integer");
  }
  if (!Number.isFinite(projection.observedAt)) errors.push("observedAt is invalid");
  if (!Number.isFinite(projection.freshUntil)) errors.push("freshUntil is invalid");
  if (
    Number.isFinite(projection.observedAt) &&
    Number.isFinite(projection.freshUntil) &&
    projection.freshUntil < projection.observedAt
  ) {
    errors.push("freshUntil precedes observedAt");
  }
  if (Number.isFinite(logicalNow) && projection.freshUntil < logicalNow) {
    errors.push("projection is stale");
  }
  if (
    !isPlainObject(projection.provenance) ||
    projection.provenance.kind !== "fake" ||
    !projection.provenance.source
  ) {
    errors.push("fake provenance is required");
  }
  if (!isPlainObject(projection.evidence) || !VALID_EVIDENCE_KINDS.has(projection.evidence.kind)) {
    errors.push("evidence kind is invalid");
  } else if (
    [EVIDENCE_KINDS.UNKNOWN, EVIDENCE_KINDS.CONFLICTING, EVIDENCE_KINDS.RECONCILIATION_REQUIRED].includes(
      projection.evidence.kind
    ) &&
    (!projection.evidence.reason || typeof projection.evidence.reason !== "string")
  ) {
    errors.push("indeterminate evidence requires a reason");
  }

  const prohibited = findProhibitedField(projection.data);
  if (prohibited) errors.push(`prohibited field: ${prohibited}`);
  const fieldResult = validateWorkspaceToolFields(tool, projection.data);
  errors.push(...fieldResult.errors);

  return { valid: errors.length === 0, errors };
}

export function assertWorkspaceToolProjection(tool, projection, options) {
  const result = validateWorkspaceToolProjection(tool, projection, options);
  if (!result.valid) throw new Error(result.errors.join("; "));
  return projection;
}

// Fail-closed presentation status for one tool projection. Cross-Account
// mismatch and staleness are evaluated before evidence.kind, matching the
// same "check the thing that must never leak first" ordering already
// established by authorizationComposition.js's evaluateAuthorizationAdmission
// and controllerFenceComposition.js's evaluateAttemptMatch, so a foreign-
// Account or stale projection's evidence/data specifics are never used to
// decide rendering. `data` is populated only for the "ok" status; every
// exceptional status carries null data so a consumer cannot accidentally
// render withheld content by ignoring `status`.
export const WORKSPACE_TOOL_PROJECTION_STATUSES = Object.freeze({
  OK: "ok",
  MISSING: "missing",
  RESTRICTED: "restricted",
  STALE: "stale",
  CONFLICTING: "conflicting",
  RECONCILIATION_REQUIRED: "reconciliation-required",
  PENDING: "pending",
  UNKNOWN: "unknown",
  CROSS_ACCOUNT_MISMATCH: "cross-account-mismatch",
});

export function resolveWorkspaceToolProjection(tool, projection, { expectedAccountId, logicalNow } = {}) {
  if (!projection) {
    return { status: WORKSPACE_TOOL_PROJECTION_STATUSES.MISSING, reason: "projection_absent", data: null };
  }
  if (expectedAccountId && projection.accountId !== expectedAccountId) {
    return {
      status: WORKSPACE_TOOL_PROJECTION_STATUSES.CROSS_ACCOUNT_MISMATCH,
      reason: "account_scope_mismatch",
      data: null,
    };
  }
  if (
    Number.isFinite(logicalNow) &&
    Number.isFinite(projection.freshUntil) &&
    projection.freshUntil < logicalNow
  ) {
    return { status: WORKSPACE_TOOL_PROJECTION_STATUSES.STALE, reason: "projection_stale", data: null };
  }

  const validation = validateWorkspaceToolProjection(tool, projection, { expectedAccountId, logicalNow });
  if (!validation.valid) {
    return { status: WORKSPACE_TOOL_PROJECTION_STATUSES.UNKNOWN, reason: validation.errors.join("; "), data: null };
  }

  const kind = projection.evidence.kind;
  switch (kind) {
    case EVIDENCE_KINDS.KNOWN:
      return { status: WORKSPACE_TOOL_PROJECTION_STATUSES.OK, reason: null, data: projection.data };
    case EVIDENCE_KINDS.MISSING:
      return {
        status: WORKSPACE_TOOL_PROJECTION_STATUSES.MISSING,
        reason: projection.evidence.reason || null,
        data: null,
      };
    case EVIDENCE_KINDS.RESTRICTED:
      return {
        status: WORKSPACE_TOOL_PROJECTION_STATUSES.RESTRICTED,
        reason: projection.evidence.reason || null,
        data: null,
      };
    case EVIDENCE_KINDS.CONFLICTING:
      return {
        status: WORKSPACE_TOOL_PROJECTION_STATUSES.CONFLICTING,
        reason: projection.evidence.reason,
        data: null,
      };
    case EVIDENCE_KINDS.RECONCILIATION_REQUIRED:
      return {
        status: WORKSPACE_TOOL_PROJECTION_STATUSES.RECONCILIATION_REQUIRED,
        reason: projection.evidence.reason,
        data: null,
      };
    case EVIDENCE_KINDS.PENDING:
      return {
        status: WORKSPACE_TOOL_PROJECTION_STATUSES.PENDING,
        reason: projection.evidence.reason || null,
        data: null,
      };
    case EVIDENCE_KINDS.FAILURE:
      return {
        status: WORKSPACE_TOOL_PROJECTION_STATUSES.UNKNOWN,
        reason: projection.evidence.reason || "failure",
        data: null,
      };
    default:
      return {
        status: WORKSPACE_TOOL_PROJECTION_STATUSES.UNKNOWN,
        reason: "unrecognized-evidence-kind",
        data: null,
      };
  }
}
